import { useEffect, useState } from "react";
import { addClassMember, getClassMembers, listMembers } from "../lib/api/endpoints";
import type { ClassDto, ClassMemberDetailsDto, MemberDto } from "../types";
import { Button } from "./Button";
import { C, fieldStyle } from "../theme";
import { NextClassSuggestion } from "./NextClassSuggestion";
import { ClassSessionsPanel } from "./ClassSessionsPanel";
import { Snackbar } from "./Snackbar";

interface ClassEnrollmentPanelProps {
  token: string;
  item: ClassDto;
  onChanged: () => void;
}

export function ClassEnrollmentPanel({ token, item, onChanged }: ClassEnrollmentPanelProps) {
  const [students, setStudents] = useState<MemberDto[]>([]);
  const [enrolled, setEnrolled] = useState<ClassMemberDetailsDto[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const [memberPage, classMembers] = await Promise.all([
        listMembers(token, { page: 1, pageSize: 100 }),
        getClassMembers(token, item.id),
      ]);
      setStudents(memberPage.items);
      setEnrolled(classMembers);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Não foi possível carregar os alunos da aula.");
    }
  };

  useEffect(() => { void load(); }, [token, item.id]);

  const enroll = async () => {
    if (!selectedStudentId || item.enrolledCount >= item.capacity) return;
    setError("");
    try {
      await addClassMember(token, item.id, selectedStudentId);
      setSelectedStudentId("");
      await load();
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Não foi possível vincular o aluno.");
    }
  };

  const availableStudents = students.filter(student => !enrolled.some(member => member.memberId === student.id));
  return <div style={{ display: "grid", gap: 8, borderTop: `1px solid ${C.faint}`, paddingTop: 8 }}>
    <div style={{ display: "flex", gap: 8 }}>
      <select aria-label={`Aluno para ${item.name}`} value={selectedStudentId} onChange={event => setSelectedStudentId(event.target.value)} style={{ ...fieldStyle, flex: 1 }}>
        <option value="">Selecionar aluno</option>
        {availableStudents.map(student => <option key={student.id} value={student.id}>{student.name}</option>)}
      </select>
      <Button onClick={() => void enroll()} disabled={!selectedStudentId || item.enrolledCount >= item.capacity}>Adicionar</Button>
    </div>
    {item.enrolledCount >= item.capacity && <span style={{ color: C.red, fontSize: 11 }}>A turma atingiu o limite de alunos.</span>}
    {item.enrolledCount >= item.capacity && <NextClassSuggestion token={token} classId={item.id} />}
    {error && <Snackbar message={error === "Failed to fetch" ? "Não foi possível conectar à API. Verifique se o servidor está ativo e tente novamente." : error} onClose={() => setError("")} />}
    {enrolled.length === 0 ? <span style={{ color: C.light, fontSize: 12 }}>Nenhum aluno vinculado.</span> : enrolled.map(member => <div key={member.memberId} style={{ color: C.gray, fontSize: 12 }}>{member.name}{member.email ? ` · ${member.email}` : ""}</div>)}
    <ClassSessionsPanel token={token} item={item} />
  </div>;
}
